import {
  Injectable,
  OnDestroy,
  PLATFORM_ID,
  Inject,
} from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import {
  BehaviorSubject,
  Observable,
  of,
  Subscription,
  throwError,
} from "rxjs";
import { ActivatedRoute, Router } from "@angular/router";
import {
  SiteDataModel,
  ComponentInfoModel,
  DropModel,
  SiteNewModel,
  CompanyLogo,
  MenuItemModel,
} from "../models/site-data.model";
import { ThemeModel } from "../models/theme.model";
import { environment } from "../../../environments/environment";
import { catchError, map, retry } from "rxjs/operators";
import { SiteModel } from "../models/builder.model";
import { SentryService } from "./sentry.service";
import {
  makeStateKey,
  StateKey,
  TransferState,
} from "@angular/platform-browser";
import { isPlatformBrowser, isPlatformServer } from "@angular/common";

import { CustomerIoService } from './customer-io.service';
import { componentModule } from '../data/builder/component-module.json';

export interface AllDataModel {
  id?: any;
  username: string;
  siteName: string;
  // companyName: string;
  // companyLogo?: string;
  modules: string[];
  siteData: SiteDataModel;
  siteStyle: ThemeModel;
  fontFamily: string;
}
@Injectable({
  providedIn: "root",
})
export class DataService implements OnDestroy {
  siteDataAndModulesSource$: BehaviorSubject<AllDataModel> =
    new BehaviorSubject(null);
  siteDataSource$: BehaviorSubject<SiteDataModel> = new BehaviorSubject(null);
  siteId$: BehaviorSubject<string> = new BehaviorSubject(null);
  site$: BehaviorSubject<SiteModel> = new BehaviorSubject(null);
  instagram$: BehaviorSubject<object> = new BehaviorSubject(null);
  position$: BehaviorSubject<string> = new BehaviorSubject(null);
  dropEvent$: BehaviorSubject<DropModel> = new BehaviorSubject(null);
  dropScrollEvent$: BehaviorSubject<number> = new BehaviorSubject(null);
  moveDownModule$: BehaviorSubject<{
    newPosition: number;
    oldPosition: number;
  }> = new BehaviorSubject(null);
  moveUpModule$: BehaviorSubject<{ newPosition: number; oldPosition: number }> =
    new BehaviorSubject(null);
  deleteModule$: BehaviorSubject<number> = new BehaviorSubject(null);
  duplicateModule$: BehaviorSubject<number> = new BehaviorSubject(null);
  NewDropModelEvent$: BehaviorSubject<ComponentInfoModel> = new BehaviorSubject(
    null
  );
  editLogo$: BehaviorSubject<CompanyLogo> = new BehaviorSubject(null);
  editMenu$: BehaviorSubject<MenuItemModel[]> = new BehaviorSubject(null);
  dropModule$: BehaviorSubject<{ event: any; position: number }> =
    new BehaviorSubject(null);
  // tslint:disable-next-line:max-line-length
  public siteModule$: Observable<string[]>;
  public siteData$: Observable<any>;
  public siteOnProd$: BehaviorSubject<SiteModel> = new BehaviorSubject(null);
  private subscriptions: Subscription[] = [];

  // Dialog Modules
  private moduleComponent$: BehaviorSubject<ComponentInfoModel> =
    new BehaviorSubject(null);
  private reloadModule$: BehaviorSubject<string> = new BehaviorSubject(null);
  changeModule = this.moduleComponent$.asObservable();
  reloadModule = this.reloadModule$.asObservable();

  newModel = "/assets/defaultModels.json";

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private transferState: TransferState,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: any,
    private sentryService: SentryService,
    private cioService: CustomerIoService,
  ) {
    this.subscriptions.push(
      this.getSiteId().subscribe((siteId) => {
        this.siteDataAndModulesSource$.next(null);
        this.siteDataSource$.next(null);
      })
    );
  }

  ngOnDestroy(): void {
    // Might cause issues with multiple components...
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  public setSiteId(siteId: string): void {
    this.siteId$.next(siteId);
  }

  public setSite(siteData: SiteModel): void {
    this.site$.next(siteData);
  }

  public getSiteId(): Observable<string> {
    return this.siteId$.asObservable();
  }

  public getCurrentSite(): Observable<SiteModel> {
    return this.site$.asObservable();
  }

  getNewModels(): Observable<SiteNewModel> {
    return this.http
      .get<SiteNewModel>(this.newModel, { headers: { Anonymous: "Anonymous" } })
      .pipe(
        catchError((err) => {
          this.handleError(err);
          return throwError(err);
        })
      );
  }

  getAllData(): Observable<AllDataModel> {
    return this.siteDataAndModulesSource$.asObservable();
  }

  setAllData(value): void {
    this.siteDataAndModulesSource$.next(value);
  }

  getSiteData(): Observable<SiteDataModel> {
    return this.siteDataSource$.asObservable();
  }

  setSiteData(value): void {
    this.siteDataSource$.next(value);
  }

  changeModules(module: ComponentInfoModel) {
    this.moduleComponent$.next(module);
  }

  reloadModules(value: string) {
    this.reloadModule$.next(value);
  }

  getDropEvent(): Observable<DropModel> {
    return this.dropEvent$.asObservable();
  }

  setDropEvent(event: DropModel) {
    this.dropEvent$.next(event);
  }

  getDropScrollEvent(): Observable<number> {
    return this.dropScrollEvent$.asObservable();
  }

  setDropScrollEvent(scrollY: number) {
    this.dropScrollEvent$.next(scrollY);
  }

  getMoveUpModule(): Observable<{ newPosition: number; oldPosition: number }> {
    return this.moveUpModule$.asObservable();
  }

  setMoveUpModule(moveUp: { newPosition: number; oldPosition: number }) {
    this.moveUpModule$.next(moveUp);
  }

  getMoveDownModule(): Observable<{
    newPosition: number;
    oldPosition: number;
  }> {
    return this.moveDownModule$.asObservable();
  }

  setMoveDownModule(moveDown: { newPosition: number; oldPosition: number }) {
    this.moveDownModule$.next(moveDown);
  }

  getDeleteModule(): Observable<number> {
    return this.deleteModule$.asObservable();
  }

  setDeleteModule(position) {
    this.deleteModule$.next(position);
  }

  getDuplicateModule(): Observable<number> {
    return this.duplicateModule$.asObservable();
  }

  setDuplicateModule(position) {
    this.duplicateModule$.next(position);
  }

  getNewDropModule(): Observable<ComponentInfoModel> {
    return this.NewDropModelEvent$.asObservable();
  }

  setNewDropModule(newModel) {
    this.NewDropModelEvent$.next(newModel);
  }

  getImagesGallery(urlGallery) {
    const headers = {
      headers: new HttpHeaders({
        "Content-Type": "application/json; charset=utf-8",
        Anonymous: "Anonymous",
        CacheControl: "no-cache",
      }),
    };
    return this.http.get(urlGallery, headers).pipe(
      catchError((err) => {
        this.handleError(err);
        return throwError(err);
      })
    );
  }

  getLibraryImages() {
    return this.http.get(`${environment.baseUrl}/getLibraryImages`).pipe(
      catchError((err) => {
        this.handleError(err);
        return throwError(err);
      })
    );
  }

  saveNewImagesGallery(images) {
    return this.http
      .post(`${environment.baseUrl}/postGalleryImages`, images)
      .pipe(
        catchError((err) => {
          this.handleError(err);
          return throwError(err);
        })
      );
  }

  deleteImagesGallery(images) {
    return this.http
      .post(`${environment.baseUrl}/deleteGalleryImages`, images)
      .pipe(
        catchError((err) => {
          this.handleError(err);
          return throwError(err);
        })
      );
  }

  saveImagesFromLibrary(images) {
    return this.http
      .post(`${environment.baseUrl}/postImagesFromLibrary`, images)
      .pipe(
        catchError((err) => {
          this.handleError(err);
          return throwError(err);
        })
      );
  }

  getImagesBySiteId(siteId) {
    const headers = {
      headers: new HttpHeaders({
        "Content-Type": "application/json; charset=utf-8",
      }),
    };
    return this.http
      .get(`${environment.baseUrl}/getImagesBySiteId/${siteId}`, headers)
      .pipe(
        catchError((err) => {
          this.handleError(err);
          return throwError(err);
        })
      );
  }

  saveNewImage(image) {
    const headers = {
      headers: new HttpHeaders({
        "Content-Type": "application/json; charset=utf-8",
      }),
    };
    return this.http.post(`${environment.baseUrl}/saveNewImage`, image).pipe(
      catchError((err) => {
        this.handleError(err);
        return throwError(err);
      })
    );
  }
  saveLogo(image) {
    const headers = {
      headers: new HttpHeaders({
        "Content-Type": "application/json; charset=utf-8",
      }),
    };
    return this.http.post(`${environment.baseUrl}/saveLogo`, image).pipe(
      catchError((err) => {
        this.handleError(err);
        return throwError(err);
      })
    );
  }

  saveImageFromLibrary(images) {
    return this.http
      .post(`${environment.baseUrl}/saveImageFromLibrary`, images)
      .pipe(
        catchError((err) => {
          this.handleError(err);
          return throwError(err);
        })
      );
  }

  deleteImageFromLibrary(siteId, imageId) {
    return this.http
      .delete(
        `${environment.baseUrl}/deleteImageFromLibrary/${siteId}/${imageId}`
      )
      .pipe(
        catchError((err) => {
          this.handleError(err);
          return throwError(err);
        })
      );
  }

  deleteImagesForComponentDeleted(body) {
    return this.http
      .post(`${environment.baseUrl}/deleteImagesForComponentDeleted`, body)
      .pipe(
        catchError((err) => {
          this.handleError(err);
          return throwError(err);
        })
      );
  }

  deleteImagesForGalleryDeleted(body) {
    return this.http
      .post(`${environment.baseUrl}/deleteImagesForGalleryDeleted`, body)
      .pipe(
        catchError((err) => {
          this.handleError(err);
          return throwError(err);
        })
      );
  }

  getSite(siteId):Observable<SiteModel> {
    const observable = this.http
      .get<SiteModel>(`${environment.baseUrl}/site/getSite/${siteId}`)
      .pipe(
        map((result) => result),
        retry(1),
        catchError((err) => {
          this.siteHandleError.bind(this, err);
          return throwError(err);
        })
      );
    return observable;
  }

  async getSiteByRoute(siteInfo, isBuilder: boolean = false) {
    if (siteInfo && /^www\./.test(siteInfo.query)) {
      siteInfo.query = siteInfo.query.replace(/^www\./g, '');
    }
    let url = `${environment.baseUrl}/getSiteByRoute`;
    if (isBuilder) {
      url = url + "?builder=true";
    }
    if (!isPlatformBrowser(this.platformId)) {
      url = url.includes('?') ? url + "&isGzip=false" : url + "?isGzip=false"
    }
    const observable = await this.checkFromTheCache(url, siteInfo, {
      headers: { Anonymous: "Anonymous" },
    });
    return observable;
  }

  async checkFromTheCache(url, body, header) {
    const stateKey: StateKey<string> = makeStateKey<string>(url);

    if (isPlatformServer(this.platformId)) {
      body.agent = 'Server';
      const observable = this.http.post(url, body, header);
      const result = await observable.toPromise();
      this.transferState.set<any>(stateKey, result);
      return observable;
    } else if (isPlatformBrowser(this.platformId)) {

      try {
        if (this.transferState.hasKey<any>(stateKey) && Object.entries(this.transferState.get<any>(stateKey, null)).length) {
          return of(this.transferState.get<any>(stateKey, null));
        } else {
          body.agent = 'Browser';
          const observable = this.http.post(url, body, header);
          const result = await observable.toPromise();
          this.transferState.set<any>(stateKey, result);
          return observable;
        }
      } catch (error) {
        if (error && error.error && error.error.message) {
          if (error && error.error.message.includes('has expired')) {
            this.router.navigate(['suspended'], { queryParams: { type: "expired" } });
            // throw (error)
          } else if (error.error.message.includes('website is suspended')) {
            this.router.navigate(['suspended'], { queryParams: { type: "site" } });
            // throw (error)
          } else if (error.error.message.includes('Your account is suspended')) {
            this.router.navigate(['suspended'], { queryParams: { type: "user" } });
            // throw (error)
          } else if (error.error.message.includes('Your website is deactivated')) {
            this.router.navigate(["suspended"], {
              queryParams: { type: "disabled" },
            });
            // throw (error)
          }
          else {
            throw (error)
          }
        }
        else {
          throw (error)
        }
      }
    }
  }

  getDraftSite(siteId) {
    const observable = this.http
      .get(`${environment.baseUrl}/site/getDraftSite/${siteId}`)
      .pipe(
        map((result) => {
          if (result !== null && Object.keys(result).length !== 0) {
            result['siteData'] = result['siteData'].replace(/\"\/assets\/img/g, '"https://s3.amazonaws.com/ai-images.sitecreator.io/assets/img');
            result['siteData'] = result['siteData'].replace(/\"\/assets\/screenshots-components/g, '"https://s3.amazonaws.com/ai-images.sitecreator.io/assets/screenshots-components');
            result['siteData'] = result['siteData'].replace(/\"\/assets\/vd/g, '"https://s3.amazonaws.com/ai-images.sitecreator.io/assets/vd');
          }
          return result;
        }),
        retry(1),
        catchError((err) => {
          this.siteHandleError.bind(this, err);
          return throwError(err);
        })
      );
    return observable;
  }

  public siteHandleError(error) {
    let errorMessage = "";
    if (error.error instanceof ErrorEvent) {
      // client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // server-side error
      if (error.error) {
        errorMessage = `Error Code: ${error.status}\nMessage: ${error.error.message}`;
      } else {
        errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
      }
    }
    if (error.status === 403) {
      if (error.error.message.includes("Your plan has expired."))
        this.router.navigate(["suspended"], {
          queryParams: { type: "expired" },
        });
    } else if (error.status === 503) {
      if (error.error.message === "Your website is suspended.")
        this.router.navigate(["suspended"], { queryParams: { type: "site" } });
      if (error.error.message === "Your account is suspended.")
        this.router.navigate(["suspended"], { queryParams: { type: "user" } });
      if (error.error.message === "Your website is deactivated.")
        this.router.navigate(["suspended"], {
          queryParams: { type: "disabled" },
        });
    }

    return throwError(errorMessage);
  }

  updateSite(body) {
    delete body["socialMedia"];
    delete body["description"];
    delete body["siteStatus"];
    delete body["suspend"];
    delete body["termsofService"];
    delete body["privacyPolicy"];
    return this.http.put(`${environment.baseUrl}/site`, body).pipe(
      catchError((err) => {
        this.handleError(err);
        return throwError(err);
      })
    );
  }

  updateSiteAsDraft(site) {
    let body = { ...site };
    delete body["socialMedia"];
    delete body["description"];
    delete body["siteStatus"];
    delete body["suspend"];
    delete body["termsofService"];
    delete body["privacyPolicy"];
    return this.http.put(`${environment.baseUrl}/updateSiteAsDraft`, body).pipe(
      catchError((err) => {
        this.handleError(err);
        return throwError(err);
      })
    );
  }

  postNewsletter(body) {
    return this.http.post(`${environment.baseUrl}/contact/newsletter`, body, { "headers": { Anonymous: 'Anonymous' } }).pipe(
      catchError(err => { this.handleError(err); return throwError(err); })
    );
  }

  postLeads(body) {
    return this.http.post(`${environment.baseUrl}/contact/leads`, body, { "headers": { Anonymous: 'Anonymous' } }).pipe(
      catchError(err => { this.handleError(err); return throwError(err); })
    );
  }

  postcontactUs(body) {
    return this.http.post(`${environment.baseUrl}/contactUs/form`, body, { "headers": { Anonymous: 'Anonymous' } }).pipe(
      catchError(err => { this.handleError(err); return throwError(err); })
    );
  }

  getEditedLogo() {
    return this.editLogo$.asObservable();
  }

  setEditedLogo(logo) {
    this.editLogo$.next(logo);
  }

  getEditedMenu() {
    return this.editMenu$.asObservable();
  }

  setEditedMenu(menu) {
    this.editMenu$.next(menu);
  }

  public handleError(error) {
    this.sentryService.errorHandling(
      error,
      "data-service",
      error.error.message
        ? error.error.message
        : error.message
          ? error.message
          : error
    );
    return throwError(error);
  }


  setDropModule(event, position) {
    this.dropModule$.next({ event, position });
  }

  getDropModule() {
    return this.dropModule$.asObservable();
  }

  getComponentsNames(componentName: string) {
    let componentsNamesEdited = [];
    componentModule.forEach(element => { componentsNamesEdited.push(element.infoModel.name) });
    if (componentName === 'Builder') {
      return componentsNamesEdited;
    } else {
      const index = componentsNamesEdited.indexOf(componentName);
      componentsNamesEdited.splice(index, 1);
      return componentsNamesEdited;
    }
  }
  getDefaultColor(colorHex) {
    const hexToRgbA = (hex) => {
      var c;
      if (!/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
        const siteTheme: ThemeModel =
          typeof this.site$.getValue().siteStyle == "string"
            ? JSON.parse(this.site$.getValue().siteStyle)
            : this.site$.getValue().siteStyle;
        ;
        if(hex){
          if (hex && hex.includes('primary-bg')) {
            hex = siteTheme.primaryBg
          }
          else if (hex.includes('primary-text')) {
            hex = siteTheme.primaryText
          }
          else if (hex.includes('secondary-text')) {
            hex = siteTheme.secondaryText
          }
          else if (hex.includes('secondary-bg')) {
            hex = siteTheme.secondaryBg
          }
          else if (hex.includes('accent')) {
            hex = siteTheme.accent
          }
          else if (hex.includes('gradient-bg')) {
            hex = siteTheme.gradientBg
          }
          else if (hex.includes('gradient-image')) {
            hex = siteTheme.gradientImage
          }
          else {
            return undefined
          }
        }
        else {
          return undefined
        }
      }
      c = hex.substring(1).split('');
      if (c.length == 3) {
        c = [c[0], c[0], c[1], c[1], c[2], c[2]];
      }
      c = '0x' + c.join('');
      return {
        red: (c >> 16) & 255,
        green: (c >> 8) & 255,
        blue: c & 255
      }
    }
    const rgb = hexToRgbA(colorHex)
    if (rgb) {
      if ((rgb.red * 0.299 + rgb.green * 0.587 + rgb.blue * 0.114) > 186) {
        return '#000000'
      }
      else {
        return '#ffffff'
      }
    }
    else {
      return ''
    }
  }
}
